import { useEffect } from 'react';
import { useBarcodeScanner } from 'lib/contexts/BarcodeScannerContext';

/**
 * Register a barcode scan handler with the global scanner
 *
 * This hook provides a convenient way to register a component's barcode scan handler
 * with the global scanner system. Handlers are automatically registered/unregistered
 * based on component lifecycle and the enabled flag. This help prevents race conditions
 * from multiple scanner instances by maintaining a registry of handlers and routing scans
 * to the highest priority active handler.
 *
 * @param {string} handlerId - Unique identifier for this handler
 * @param {Function} onScan - Callback when barcode is scanned
 * @param {Object} options - Configuration options
 * @param {number} options.priority - Handler priority (higher = more important, default: 0)
 * @param {Object} options.scanOptions - onScan.js options (minLength, etc., default: {})
 * @param {boolean} options.enabled - Whether this handler is active (default: true)
 *
 * @example
 * // Modal with high priority, only active when open
 * useBarcodeScanHandler('selectActivity', handleScan, {
 *   priority: 100,
 *   enabled: modalOpen,
 * });
 *
 * @example
 * // Page with dynamic scan options
 * useBarcodeScanHandler('selectWorkOrder', handleScan, {
 *   priority: 90,
 *   enabled: modalOpen,
 *   scanOptions: { minLength: hasWorkOrderId ? 1 : 3 },
 * });
 */
const useBarcodeScanHandler = (handlerId, onScan, options = {}) => {
  const { registerHandler } = useBarcodeScanner();
  const { enabled = true, priority = 0, scanOptions = {} } = options;

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    return registerHandler(handlerId, onScan, {
      priority,
      scanOptions,
    });
  }, [handlerId, onScan, enabled, priority, scanOptions, registerHandler]);
};

export default useBarcodeScanHandler;
